/**
 * Adapts the share hook's invite state onto the invite row view (SE-8313).
 * @module features/scouting/components/molecules/ShareInviteRow/ShareInviteRowContainer
 */

import ShareInviteRowView from "./ShareInviteRowView";

import type { ShareGroupCandidate } from "@/features/scouting/utils/shareGroupGrants";
import {
  SHARE_ROLE_OPTIONS,
  shareRoleOptionLabel,
  type ShareCandidateItem,
  type ShareGrantRole,
} from "@/features/scouting/utils/shareWatchlistGrants";

/** Props for `ShareInviteRowContainer`. */
export interface ShareInviteRowContainerProps {
  /** Current search text. */
  readonly searchQuery: string;
  /** Suggestions for the current search. */
  readonly candidates: readonly ShareCandidateItem[];
  /** The picked suggestion, if any. */
  readonly selectedCandidate: ShareCandidateItem | null;
  /**
   * "Everyone at {organization}" when the owner may still invite it
   * (SE-8359); pinned first among the suggestions. Absent = not offered.
   */
  readonly groupCandidate?: ShareGroupCandidate | null;
  /** The picked group, if any (shown as a pill, like a picked person). */
  readonly selectedGroup?: ShareGroupCandidate | null;
  /** Picks the group candidate. */
  readonly onSelectGroup?: () => void;
  /** Role the next invite grants. */
  readonly inviteRole: ShareGrantRole;
  /** Inline error, or null. */
  readonly inviteError: string | null;
  /** Whether an invite is in flight. */
  readonly isInviting: boolean;
  /** Search change handler. */
  readonly onSearchChange: (next: string) => void;
  /** Suggestion pick handler. */
  readonly onSelectCandidate: (userId: string) => void;
  /** Clears the picked suggestion. */
  readonly onClearCandidate: () => void;
  /** Role Select change handler. */
  readonly onInviteRoleChange: (value: string) => void;
  /** Invite press handler. */
  readonly onInvite: () => void;
}

/**
 * Renders the invite row with the role vocabulary resolved.
 * @param props - Component props (see {@link ShareInviteRowContainerProps})
 * @returns The invite row view.
 */
const ShareInviteRowContainer = (props: ShareInviteRowContainerProps) => (
  <ShareInviteRowView
    searchQuery={props.searchQuery}
    candidates={props.candidates}
    selectedCandidate={props.selectedCandidate}
    groupCandidate={props.groupCandidate ?? null}
    selectedGroup={props.selectedGroup ?? null}
    onSelectGroup={props.onSelectGroup}
    inviteRole={props.inviteRole}
    inviteRoleLabel={shareRoleOptionLabel(props.inviteRole)}
    roleOptions={SHARE_ROLE_OPTIONS}
    inviteError={props.inviteError}
    isInviting={props.isInviting}
    onSearchChange={props.onSearchChange}
    onSelectCandidate={props.onSelectCandidate}
    onClearCandidate={props.onClearCandidate}
    onInviteRoleChange={props.onInviteRoleChange}
    onInvite={props.onInvite}
  />
);

ShareInviteRowContainer.displayName = "ShareInviteRowContainer";

export default ShareInviteRowContainer;
